import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Form, Table } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { numberFormat } from "../../utils/numberFormat";
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from "moment";
import { useParams } from "react-router-dom";
import { fetchAllDataAPI, filterByParams } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";

const CenterHeadPerformance = () => {
    const { slug } = useParams();
    const [report, setReport] = useState([]);
    const [filteredReport, setFilteredReport] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toLocaleDateString('en-US'),
        endDate: new Date().toLocaleDateString('en-US'),
    });

    useEffect(() => {
        getCenterHeadPerformanceReport(slug);
    }, [slug]);

    const getCenterHeadPerformanceReport = async (slug) => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI(`ppc-get-center-head-performance-report-last-two-months/${slug}`);
            if (res.data.status === 1) {
                setReport(res.data.report);
                setFilteredReport(res.data.report);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
        }
        setIsLoading(false);
    };

    const handleFilterByDate = async (event, picker) => {
        setIsLoading(true);
        const selectedRange = {
            slug,
            startDate: picker.startDate.format("YYYY-MM-DD"),
            endDate: picker.endDate.format("YYYY-MM-DD")
        };
        setDateRange(selectedRange);
        try {
            const res = await filterByParams("ppc-get-filter-center-head-performance-report-last-two-months", selectedRange);
            if (res.data.status === 1) {
                setReport(res.data.report);
                setFilteredReport(res.data.report);
            }
        } catch (err) {
            console.error(err);
        }
        setIsLoading(false);
    };

    const handleReset = () => window.location.reload();

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const filteredData = report.filter(item =>
            item.cluster_head_name?.toLowerCase().includes(value)
        );
        setFilteredReport(filteredData);
    };

    const columns = [
        { id: 's_no', displayName: 'Serial No' },
        { id: 'cluster_head_name', displayName: 'Center Head Name' },
        { id: 'month_1_value', displayName: filteredReport[0]?.last_month_name || "Month 1" },
        { id: 'month_2_value', displayName: filteredReport[0]?.current_month_name || "Month 2" },
        { id: 'growth_percentage', displayName: 'Growth (%)' },
        { id: 'yesterday_total', displayName: 'Yesterday Total Order' },
        { id: 'delivery_percentage', displayName: 'Delivered (%)' },
        { id: 'block_01_10_delivery_percentage', displayName: '01–10 (%)' },
        { id: 'block_11_20_delivery_percentage', displayName: '11–20 (%)' },
        { id: 'block_21_till_delivery_percentage', displayName: '21–31 (%)' },
        { id: 'ticket_size', displayName: 'Ticket Size' },
    ];

    const sortedReport = [...filteredReport].sort((a, b) => {
        if (a.cluster_head_name?.toLowerCase() === 'grand_total') return 1;
        if (b.cluster_head_name?.toLowerCase() === 'grand_total') return -1;
        return a.cluster_head_name.localeCompare(b.cluster_head_name);
    });

    const exportReport = sortedReport.map((item, index) => ({
        s_no: index + 1,
        cluster_head_name: item.cluster_head_name,
        month_1_value: item.last_month_total || 0,
        month_2_value: item.current_month_total || 0,
        growth_percentage: item.growth_percentage !== null ? Number(item.growth_percentage).toFixed(2) : 0,
        yesterday_total: item.yesterday_total || 0,
        delivery_percentage: item.delivery_percentage !== null ? Number(item.delivery_percentage).toFixed(2) : 0,
        block_01_10_delivery_percentage: item.block_01_10_delivery_percentage !== null ? Number(item.block_01_10_delivery_percentage).toFixed(2) : 0,
        block_11_20_delivery_percentage: item.block_11_20_delivery_percentage !== null ? Number(item.block_11_20_delivery_percentage).toFixed(2) : 0,
        block_21_till_delivery_percentage: item.block_21_till_delivery_percentage !== null ? Number(item.block_21_till_delivery_percentage).toFixed(2) : 0,
        ticket_size: item.ticket_size || 0,
    }));

    const grandTotal = filteredReport.find(item => item.cluster_head_name.toLowerCase() === 'grand_total');

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR: CENTER HEAD PERFORMANCE</title>
            </Helmet>
            <ContentWrapper>
                <ReportNavBar />
                <div className="card p-2" style={{ marginBottom: "50px" }}>
                    <div className="heading d-flex justify-content-between mb-2">
                        <h5 className="mb-0 text-uppercase mt-2">
                            Center Head Performance Report from {filteredReport[0]?.last_month_name || 'Last Month'} to {filteredReport[0]?.current_month_name || 'This Month'}
                        </h5>
                        <div className="mt-1">
                            <Form.Control
                                type="text"
                                placeholder="Search by Cluster Head Name"
                                value={searchTerm}
                                onChange={handleSearch}
                                className="mb-3"
                            />
                        </div>
                        <div className="d-flex">
                            <span onClick={handleReset} className="btnreset">Reset</span>
                            <div className="btnpicker">
                                <DateRangePicker
                                    initialSettings={{
                                        startDate: dateRange.startDate,
                                        endDate: dateRange.endDate,
                                    }}
                                    onApply={handleFilterByDate}
                                >
                                    <button className="btn btn-md btndaterange">Select Date Range</button>
                                </DateRangePicker>
                            </div>
                        </div>
                    </div>
                    <div className="table pagetable table-responsive">
                        <Table className="table-sm table-fixeds" responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Center Head Name</th>
                                    <th>{filteredReport[0]?.last_month_name || "Last Month"}</th>
                                    <th>{filteredReport[0]?.current_month_name || "Current Month"} Till Date</th>
                                    <th>Growth (%)</th>
                                    <th>Yesterday Total Order</th>
                                    <th>Delivered(%)</th>

                                    <th>
                                        01–10{" "}
                                        {moment(dateRange?.endDate).format("MMM")}(%)
                                    </th>
                                    <th>
                                        11–20{" "}
                                        {moment(dateRange?.endDate).format("MMM")}(%)
                                    </th>
                                    <th>
                                        21–{moment(dateRange?.endDate).endOf("month").format("DD")}{" "}
                                        {moment(dateRange?.endDate).format("MMM")}(%)
                                    </th>

                                    <th>Ticket Size</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    [...Array(5)].map((_, index) => (
                                        <tr key={index}>
                                            {Array.from({ length: 11 }).map((_, i) => (
                                                <td key={i}><Skeleton animation="wave" variant="text" /></td>
                                            ))}
                                        </tr>
                                    ))
                                ) : filteredReport.length > 0 ? (
                                    filteredReport
                                        .filter(item => item.cluster_head_name.toLowerCase() !== 'grand_total')
                                        .map((item, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{item.cluster_head_name}</td>
                                                <td>{numberFormat(item.last_month_total)}</td>
                                                <td>{numberFormat(item.current_month_total)}</td>
                                                <td style={{ fontWeight: "600", color: item.growth_percentage >= 0 ? "green" : "red" }}>
                                                    {item.growth_percentage !== null ? Number(item.growth_percentage).toFixed(2) + "%" : "N/A"}
                                                </td>
                                                <td>{numberFormat(item.yesterday_total)}</td>
                                                <td
                                                    style={{
                                                        color: item.delivery_percentage >= 50 ? "white" : "inherit",
                                                        background: item.delivery_percentage >= 50 ? "green" : "inherit",
                                                        fontWeight: item.delivery_percentage >= 50 ? "bold" : "normal"
                                                    }}
                                                >
                                                    {item.delivery_percentage ? item.delivery_percentage + "%" : "N/A"}
                                                </td>
                                                <td
                                                    style={{
                                                        color: item.block_01_10_delivery_percentage >= 50 ? "white" : "inherit",
                                                        background: item.block_01_10_delivery_percentage >= 50 ? "green" : "inherit",
                                                        fontWeight: item.block_01_10_delivery_percentage >= 50 ? "bold" : "normal"
                                                    }}
                                                >
                                                    {item.block_01_10_delivery_percentage ? item.block_01_10_delivery_percentage + "%" : "N/A"}
                                                </td>
                                                <td
                                                    style={{
                                                        color: item.block_11_20_delivery_percentage >= 50 ? "white" : "inherit",
                                                        background: item.block_11_20_delivery_percentage >= 50 ? "green" : "inherit",
                                                        fontWeight: item.block_11_20_delivery_percentage >= 50 ? "bold" : "normal"
                                                    }}
                                                >
                                                    {item.block_11_20_delivery_percentage ? item.block_11_20_delivery_percentage + "%" : "N/A"}
                                                </td>
                                                <td
                                                    style={{
                                                        color: item.block_21_till_delivery_percentage >= 50 ? "white" : "inherit",
                                                        background: item.block_21_till_delivery_percentage >= 50 ? "green" : "inherit",
                                                        fontWeight: item.block_21_till_delivery_percentage >= 50 ? "bold" : "normal"
                                                    }}
                                                >
                                                    {item.block_21_till_delivery_percentage ? item.block_21_till_delivery_percentage + "%" : "N/A"}
                                                </td>
                                                <td>{numberFormat(item.ticket_size) || "N/A"}</td>
                                            </tr>
                                        ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="text-center">No data available</td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                {grandTotal && (
                                    <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                                        <th colSpan={1} style={{ fontWeight: "600", color: "#000" }}>#</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>Grand Total</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{numberFormat(grandTotal.last_month_total)}</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{numberFormat(grandTotal.current_month_total)}</th>
                                        <th style={{ fontWeight: "600", color: grandTotal.growth_percentage >= 0 ? "green" : "red" }}>
                                            {grandTotal.growth_percentage !== null ? Number(grandTotal.growth_percentage).toFixed(2) + "%" : "N/A"}
                                        </th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{numberFormat(grandTotal.yesterday_total)}</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{grandTotal.delivery_percentage ? grandTotal.delivery_percentage + "%" : "N/A"}</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{grandTotal.block_01_10_delivery_percentage ? grandTotal.block_01_10_delivery_percentage + "%" : "N/A"}</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{grandTotal.block_11_20_delivery_percentage ? grandTotal.block_11_20_delivery_percentage + "%" : "N/A"}</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{grandTotal.block_21_till_delivery_percentage ? grandTotal.block_21_till_delivery_percentage + "%" : "N/A"}</th>
                                        <th style={{ fontWeight: "600", color: "#000" }}>{numberFormat(grandTotal.ticket_size)}</th>
                                    </tr>
                                )}
                            </tfoot>
                        </Table>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(CenterHeadPerformance);
